import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useIngresosOptimistic } from '../hooks/useIngresosOptimistic';
import { useAuth } from '../../auth/hooks/useAuth';
import { LoggerService } from '../../../shared/services/logger.service';

interface IngresoFormData {
  concepto: string;
  monto: number;
  categoria: string;
  fecha: string;
  descripcion?: string;
}

export const ModernIngresosDashboard: React.FC = () => {
  const { ingresos, isLoading, error, createIngreso, deleteIngreso } = useIngresosOptimistic();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [minMonto, setMinMonto] = useState<string>('');
  const [maxMonto, setMaxMonto] = useState<string>('');
  const now = new Date();
  const [mesSeleccionado, setMesSeleccionado] = useState(now.getMonth());
  const [añoSeleccionado, setAñoSeleccionado] = useState(now.getFullYear());
  const [formData, setFormData] = useState<IngresoFormData>({
    concepto: '',
    monto: 0,
    categoria: '',
    fecha: new Date().toISOString().split('T')[0],
    descripcion: ''
  });

  // Detectar filtro de categoría desde URL
  useEffect(() => {
    const categoria = searchParams.get('categoria');
    if (categoria) {
      setCategoriaFiltro(categoria);
    }
  }, [searchParams]);

  // Funciones de navegación mensual
  const cambiarMes = (direccion: 'anterior' | 'siguiente') => {
    if (direccion === 'anterior') {
      if (mesSeleccionado === 0) {
        setMesSeleccionado(11);
        setAñoSeleccionado(añoSeleccionado - 1);
      } else {
        setMesSeleccionado(mesSeleccionado - 1);
      }
    } else {
      if (mesSeleccionado === 11) {
        setMesSeleccionado(0);
        setAñoSeleccionado(añoSeleccionado + 1);
      } else {
        setMesSeleccionado(mesSeleccionado + 1);
      }
    }
  };

  const volverMesActual = () => {
    setMesSeleccionado(now.getMonth());
    setAñoSeleccionado(now.getFullYear());
  };

  const nombreMes = new Date(añoSeleccionado, mesSeleccionado).toLocaleDateString('es-AR', { 
    month: 'long', 
    year: 'numeric' 
  });

  // Filtrar y paginar ingresos
  const ingresosFiltrados = ingresos.filter(ingreso => {
    const ingresoDate = new Date(ingreso.fecha);
    const matchMes = ingresoDate.getMonth() === mesSeleccionado && ingresoDate.getFullYear() === añoSeleccionado;
    const matchCategoria = !categoriaFiltro || ingreso.categoria?.toLowerCase() === categoriaFiltro.toLowerCase();
    const matchSearch = !searchText || 
      ingreso.concepto?.toLowerCase().includes(searchText.toLowerCase()) ||
      ingreso.descripcion?.toLowerCase().includes(searchText.toLowerCase());
    const matchMinMonto = !minMonto || ingreso.monto >= parseFloat(minMonto);
    const matchMaxMonto = !maxMonto || ingreso.monto <= parseFloat(maxMonto);
    
    return matchMes && matchCategoria && matchSearch && matchMinMonto && matchMaxMonto;
  });

  const limpiarFiltro = () => {
    setCategoriaFiltro('');
    setSearchText('');
    setMinMonto('');
    setMaxMonto('');
  };

  // Debug logging
  console.log('ModernIngresosDashboard render:', { 
    ingresosCount: ingresos?.length, 
    isLoading, 
    error,
    hasCreateIngreso: !!createIngreso,
    hasDeleteIngreso: !!deleteIngreso,
    ingresos: ingresos,
    user: user,
    userUid: user?.uid
  });

  // Early error boundary
  if (error) {
    console.error('Error in ModernIngresosDashboard:', error);
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error al cargar ingresos</h2>
          <p className="text-red-600">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Recargar página
          </button>
        </div>
      </div>
    );
  }

  // Calcular total de ingresos del mes seleccionado
  const getCurrentMonthTotal = () => {
    return ingresosFiltrados.reduce((total, ingreso) => total + ingreso.monto, 0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getCategoryColor = (categoria: string) => {
    const categoryColors: { [key: string]: { bg: string; text: string; badge: string } } = {
      'Trabajo': { bg: 'bg-emerald-50', text: 'text-emerald-800', badge: 'bg-emerald-100' },
      'Independiente': { bg: 'bg-blue-50', text: 'text-blue-800', badge: 'bg-blue-100' },
      'Inversiones': { bg: 'bg-purple-50', text: 'text-purple-800', badge: 'bg-purple-100' },
      'Inmuebles': { bg: 'bg-orange-50', text: 'text-orange-800', badge: 'bg-orange-100' },
      'Otros': { bg: 'bg-gray-50', text: 'text-gray-800', badge: 'bg-gray-100' }
    };
    
    return categoryColors[categoria] || categoryColors['Otros'];
  };

  const handleInputChange = (field: keyof IngresoFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      LoggerService.info('Creating new ingreso', 'ModernIngresosDashboard', { formData, userId: user?.uid });
      await createIngreso({
        concepto: formData.concepto,
        monto: formData.monto,
        categoria: formData.categoria,
        fecha: new Date(formData.fecha),
        descripcion: formData.descripcion
      });
      
      // Reset form
      setFormData({
        concepto: '',
        monto: 0,
        categoria: '',
        fecha: new Date().toISOString().split('T')[0],
        descripcion: ''
      });
      setShowForm(false);
      
      LoggerService.info('Ingreso created successfully', 'ModernIngresosDashboard');
    } catch (error) {
      LoggerService.error('Failed to create ingreso', 'ModernIngresosDashboard', { error });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      LoggerService.info('Deleting ingreso', 'ModernIngresosDashboard', { id });
      await deleteIngreso(id);
      LoggerService.info('Ingreso deleted successfully', 'ModernIngresosDashboard');
    } catch (error) {
      LoggerService.error('Failed to delete ingreso', 'ModernIngresosDashboard', { error });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gestión de Ingresos</h1>
        <p className="text-gray-600 mt-1">Administra y visualiza todas tus fuentes de ingresos</p>
      </div>

      {/* Total del Mes */}
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium opacity-90">Ingresos Totales del Mes</h2>
            <p className="text-3xl font-bold mt-2">{formatCurrency(getCurrentMonthTotal())}</p>
          </div>
          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <i className="ri-money-dollar-circle-line text-2xl"></i>
          </div>
        </div>
      </div>

      {/* Add Button */}
      <button
        onClick={() => setShowForm(true)}
        className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-md"
      >
        <i className="ri-add-line"></i>
        <span>Agregar Ingreso</span>
      </button>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Nuevo Ingreso</h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Concepto
                </label>
                <input
                  type="text"
                  value={formData.concepto}
                  onChange={(e) => handleInputChange('concepto', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ej: Sueldo Principal"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto
                </label>
                <input
                  type="number"
                  value={formData.monto}
                  onChange={(e) => handleInputChange('monto', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoría
                </label>
                <select
                  value={formData.categoria}
                  onChange={(e) => handleInputChange('categoria', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Seleccionar categoría</option>
                  <option value="Trabajo">Trabajo</option>
                  <option value="Independiente">Independiente</option>
                  <option value="Inversiones">Inversiones</option>
                  <option value="Inmuebles">Inmuebles</option>
                  <option value="Otros">Otros</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha
                </label>
                <input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => handleInputChange('fecha', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción (opcional)
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => handleInputChange('descripcion', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  rows={2}
                  placeholder="Información adicional..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.concepto || !formData.categoria || formData.monto <= 0}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navegación Mensual y Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
        {/* Navegación de Mes */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => cambiarMes('anterior')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Mes anterior"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 capitalize">{nombreMes}</h3>
            <p className="text-sm text-gray-600">
              {ingresosFiltrados.length} ingreso{ingresosFiltrados.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          <button
            onClick={() => cambiarMes('siguiente')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Mes siguiente"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        {(mesSeleccionado !== now.getMonth() || añoSeleccionado !== now.getFullYear()) && (
          <button
            onClick={volverMesActual}
            className="w-full px-4 py-2 text-sm bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors"
          >
            <i className="ri-calendar-todo-line mr-2"></i>
            Volver al mes actual
          </button>
        )}

        {/* Filtros */}
        <div className="border-t pt-4 space-y-3">
          <h4 className="font-medium text-gray-900 mb-3">Filtros</h4>
          
          {/* Buscador */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar por concepto o descripción
            </label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Buscar..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Filtro por categoría */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categoría
            </label>
            <select
              value={categoriaFiltro}
              onChange={(e) => setCategoriaFiltro(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Todas las categorías</option>
              <option value="Trabajo">Trabajo</option>
              <option value="Independiente">Independiente</option>
              <option value="Inversiones">Inversiones</option>
              <option value="Inmuebles">Inmuebles</option>
              <option value="Otros">Otros</option>
            </select>
          </div>

          {/* Filtro por rango de monto */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto mínimo
              </label>
              <input
                type="number"
                value={minMonto}
                onChange={(e) => setMinMonto(e.target.value)}
                placeholder="$ 0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto máximo
              </label>
              <input
                type="number"
                value={maxMonto}
                onChange={(e) => setMaxMonto(e.target.value)}
                placeholder="$ ∞"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Botón limpiar filtros */}
          {(categoriaFiltro || searchText || minMonto || maxMonto) && (
            <button
              onClick={limpiarFiltro}
              className="w-full px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <i className="ri-close-circle-line mr-2"></i>
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Ingresos List */}
      <div className="grid gap-4">
        {ingresosFiltrados.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <i className="ri-money-dollar-circle-line text-4xl text-gray-400 mb-4"></i>
            <p className="text-gray-600">No hay ingresos registrados</p>
            <p className="text-sm text-gray-500 mt-1">Agrega tu primer ingreso para comenzar</p>
          </div>
        ) : (
          ingresosFiltrados.map((ingreso) => {
            const categoryStyle = getCategoryColor(ingreso.categoria);
            return (
              <div
                key={ingreso.id}
                className={`${categoryStyle.bg} rounded-xl p-4 border border-gray-200 hover:shadow-md transition-shadow`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{ingreso.concepto}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${categoryStyle.badge} ${categoryStyle.text}`}>
                        {ingreso.categoria}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Monto:</span>
                        <p className="font-semibold text-emerald-600 text-lg">
                          {formatCurrency(ingreso.monto)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Fecha:</span>
                        <p className="font-medium text-gray-900">
                          {formatDate(ingreso.fecha)}
                        </p>
                      </div>
                    </div>

                    {ingreso.descripcion && (
                      <p className="text-sm text-gray-600 mt-2">{ingreso.descripcion}</p>
                    )}
                  </div>

                  <button
                    onClick={() => handleDelete(ingreso.id)}
                    className="ml-4 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar ingreso"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};